/**
 * Shared internal helpers for the workspace builders.
 *
 * Time formatting (`formatRelative`, `formatAbsolute`, `formatPrefix`) and the
 * navigation-event derivation (`NAVIGATION_ACTIONS`, `hostOf`) live here so the
 * ledger, per-step rollup, summary, and JSONL writers all agree on the format
 * and the rules. Avoids the dual-validator drift pattern.
 */

/** Set of action `type` strings that count as a navigation event. */
export const NAVIGATION_ACTIONS = new Set([
  'navigate',
  'navigation',
  'pushstate',
  'popstate',
  'pushState',
  'popState',
  'redirect',
  'load',
]);

/**
 * Extract a normalized hostname from a URL, lowercased and stripped of `www.`.
 * Returns null on invalid input. Used to detect cross-host transitions
 * consistently across builders.
 */
export const hostOf = (raw: unknown): string | null => {
  if (typeof raw !== 'string' || raw.length === 0) return null;
  try {
    return new URL(raw).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return null;
  }
};

/**
 * Format a duration as `T+S.SSSs` / `T-S.SSSs` relative to a baseline.
 * Used in ledger lines, per-step rollups, and summary step rows.
 */
export const formatRelative = (ms: number): string => {
  const sign = ms < 0 ? '-' : '+';
  const abs = Math.abs(ms);
  const seconds = Math.floor(abs / 1000);
  const millis = abs % 1000;
  return `T${sign}${seconds}.${String(millis).padStart(3, '0')}s`;
};

/** Format an absolute wall-clock timestamp as `HH:MM:SS.mmm` in UTC. */
export const formatAbsolute = (ms: number): string => {
  const d = new Date(ms);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  const mil = String(d.getUTCMilliseconds()).padStart(3, '0');
  return `${hh}:${mm}:${ss}.${mil}`;
};

/**
 * Format a timestamp prefix with both relative and absolute times, suitable
 * for ledger lines and per-step rollup events. Falls back to `[unknown @ unknown]`
 * when timestamps are unknown.
 */
export const formatPrefix = (
  timestampMs: number | null,
  baselineMs: number | null,
): string => {
  if (timestampMs === null) return '[unknown @ unknown]';
  const rel = baselineMs !== null ? formatRelative(timestampMs - baselineMs) : 'T+?';
  const abs = formatAbsolute(timestampMs);
  return `[${rel} @ ${abs}]`;
};

/** Collapse whitespace runs to single spaces and trim. */
export const oneLine = (s: string): string => s.replace(/\s+/g, ' ').trim();

// ============================================================================
// Step work windows
// ============================================================================

/** A step's true execution window in absolute ms. */
export interface StepWorkWindow {
  startMs: number;
  endMs: number;
}

/** Minimal step shape needed to compute work windows. */
interface WorkWindowStep {
  id: string;
  orderIndex: number;
  startedAtMs: number | null;
  finishedAtMs: number | null;
}

/** Minimal action shape needed to compute work windows. */
interface WorkWindowAction {
  /** For annotations, the human label (step name); otherwise the action type. */
  action: string;
  /** For annotations, the classifier (`step-started` / `step-completed` / …). */
  target: string | null;
  timestampMs: number | null;
  isAnnotation: boolean;
}

/**
 * A step record's `startedAt`/`finishedAt` may only reflect the *commit* moment
 * (e.g. a cache/playbook step whose row is written when the step finalizes),
 * while the real browser work ran earlier under the step's
 * `step-started`→`step-completed` annotation bracket. Windowing untyped events
 * (actions, nav, network, console) by the record bounds then drops all the real
 * work into a dead zone — invisible in the step's rollup and mis-grouped in the
 * ledger.
 *
 * This pairs consecutive `step-started`/`step-completed` annotations into ordered
 * brackets and matches each, in execution order, to the step whose `finishedAtMs`
 * lands within `toleranceMs` of the bracket's completion (the record commits ≈ at
 * `step-completed`). A matched step's work window widens to cover both the record
 * bounds and the bracket. Steps with no matching bracket (login/setup, or runs
 * without these annotations) are absent from the map — callers fall back to the
 * record bounds, preserving existing behavior when the signal is absent.
 */
export const computeStepWorkWindows = (
  steps: ReadonlyArray<WorkWindowStep>,
  actions: ReadonlyArray<WorkWindowAction>,
  toleranceMs = 2000,
): Map<string, StepWorkWindow> => {
  // Ordered (step-started, step-completed) brackets from the annotation stream.
  const sortedAnnotations = actions
    .filter(
      (a): a is WorkWindowAction & { timestampMs: number } =>
        a.isAnnotation && a.timestampMs !== null,
    )
    .slice()
    .sort((a, b) => a.timestampMs - b.timestampMs);

  const brackets: Array<{ startMs: number; endMs: number }> = [];
  let openStartMs: number | null = null;
  for (const a of sortedAnnotations) {
    if (a.target === 'step-started') {
      openStartMs = a.timestampMs;
    } else if (a.target === 'step-completed' && openStartMs !== null) {
      brackets.push({ startMs: openStartMs, endMs: a.timestampMs });
      openStartMs = null;
    }
  }

  const stepsByOrder = steps
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex);

  const windows = new Map<string, StepWorkWindow>();
  let stepCursor = 0;
  for (const bracket of brackets) {
    // Advance past steps that finished well before this bracket completed (they
    // own no bracket — keep their record bounds) and steps with no finish time.
    while (stepCursor < stepsByOrder.length) {
      const step = stepsByOrder[stepCursor]!;
      if (step.finishedAtMs === null) {
        stepCursor++;
        continue;
      }
      if (step.finishedAtMs < bracket.endMs - toleranceMs) {
        stepCursor++;
        continue;
      }
      break;
    }
    const step = stepsByOrder[stepCursor];
    if (
      step !== undefined &&
      step.finishedAtMs !== null &&
      Math.abs(step.finishedAtMs - bracket.endMs) <= toleranceMs
    ) {
      const recordStart = step.startedAtMs ?? bracket.startMs;
      windows.set(step.id, {
        startMs: Math.min(recordStart, bracket.startMs),
        endMs: Math.max(step.finishedAtMs, bracket.endMs),
      });
      stepCursor++;
    }
    // Otherwise this bracket has no step within tolerance — skip it, keep the cursor.
  }

  return windows;
};

/** Truncate to at most `max` characters, appending `…` when needed. */
export const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}…`;

// ============================================================================
// Trace clock — one absolute axis for actions and screencast frames
// ============================================================================

/** Minimal action shape the trace clock needs. */
interface TraceClockAction {
  startTime: number;
  wallTime?: number;
}

/**
 * The diagnostic workspace places every browser artifact on one absolute-ms
 * axis anchored at the run's start. Trace actions carry two clocks:
 *
 *   - `startTime` — a monotonic process clock that RESTARTS per browser segment.
 *     The legacy `runStartedAtMs + (startTime - startTime[0])` math is only
 *     correct for single-segment traces; a stitched multi-segment trace skews.
 *   - `wallTime` — wall-clock epoch ms, segment-stable, and the SAME clock the
 *     screencast `frameSwapWallTime` uses.
 *
 * `makeTraceClock` anchors both actions and screencast frames to the run-start
 * axis via wall time, so frames drop into the exact same `timestampMs` space the
 * ledger/rollups/`inWindow` already use — no cross-clock arithmetic for readers.
 * When the first action lacks `wallTime` (older/partial traces), it falls back
 * to the monotonic anchor and reports `approxAlignment = true`, which callers
 * surface so the screencast index is honestly marked.
 */
export interface TraceClock {
  /** Map a wall-clock epoch ms (action.wallTime / frame.frameSwapWallTime) to the run axis. */
  absFromWall: (wallMs: number) => number;
  /** Map a trace action to the run axis (wall when present, monotonic fallback otherwise). */
  absFromAction: (action: TraceClockAction) => number;
  /** True when no wall-clock anchor was available; screencast alignment is approximate. */
  approxAlignment: boolean;
}

export const makeTraceClock = (
  actions: ReadonlyArray<TraceClockAction>,
  runStartedAtMs: number | null,
): TraceClock => {
  const base = runStartedAtMs ?? 0;
  const first = actions[0];
  const wallAnchor =
    first && typeof first.wallTime === 'number' ? first.wallTime : null;
  const monoAnchor = first ? first.startTime : 0;

  if (wallAnchor !== null) {
    const absFromWall = (wallMs: number): number => base + (wallMs - wallAnchor);
    return {
      absFromWall,
      absFromAction: (action) =>
        typeof action.wallTime === 'number'
          ? absFromWall(action.wallTime)
          : base + (action.startTime - monoAnchor),
      approxAlignment: false,
    };
  }

  // No wall anchor (no action carried wallTime). Actions fall back to the
  // monotonic clock. Screencast `frameSwapWallTime` is already an absolute epoch
  // (and the run started at ~`base`, also an epoch), so the frame's wall time is
  // its own best absolute position — return it as-is. Both lanes then sit at
  // ≈epoch scale and stay mutually coherent, but the alignment is approximate.
  return {
    absFromWall: (wallMs: number) => wallMs,
    absFromAction: (action) => base + (action.startTime - monoAnchor),
    approxAlignment: true,
  };
};
